import { Box, Button, TextField, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useCreateUserMutation } from '@api/user';
import { EmptyResultResponse, User } from '@apiContract';
import AutocompleteLocation from '@components/AutocompleteLocation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSettings } from '@hooks/useSettings';

import { populateNameFields } from '../helper';
import { sxButton, sxFormTab, sxPT20 } from '../styles';

type FormValues = {
  firstName: string;
  lastName: string;
  userName?: string;
  autoAssignId?: string;
  email: string;
  roles: string[];
  locationId?: number;
  sendPasswordEmail?: boolean;
};

type FormProps = {
  changeTab: () => void;
  assignUser: (user?: User) => void;
  defaultSearch?: string | null;
};
const schema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  userName: yup.string(),
  autoAssignId: yup.string(),
  email: yup.string().required('Email is required').email('Invalid email format'),
  roles: yup.array().of(yup.string().required()).min(1).required(),
  locationId: yup.number(),
  sendPasswordEmail: yup.boolean(),
});

type Settings = {
  SendPasswordEmailEnable: boolean;
};

const settingsNames = ['SendPasswordEmailEnable'];

const Form: FC<FormProps> = ({ changeTab, assignUser, defaultSearch }) => {
  const [createUser, { isLoading }] = useCreateUserMutation();
  const settingsValue = useSettings(settingsNames) as Settings;
  const { firstName, lastName } = populateNameFields(defaultSearch);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    clearErrors,
  } = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: { roles: ['Recipient'] },
  });

  useEffect(() => {
    setValue('firstName', firstName);
    setValue('lastName', lastName);
    clearErrors(['firstName', 'lastName']);
  }, [setValue, defaultSearch, firstName, lastName, clearErrors]);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const updatedData = {
      ...data,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      userName: data.userName?.trim(),
    };
    createUser(updatedData)
      .unwrap()
      .then((user: User) => {
        reset();
        assignUser(user);
        changeTab();
      })
      .catch((e: { data: EmptyResultResponse }) => {
        if (e.data?.validationResult?.errors) {
          const validationErrors = e.data.validationResult.errors;
          Object.keys(validationErrors).forEach((key) => {
            const errorTitle = validationErrors[key].join(', ');
            setError(key as keyof FormValues, { message: errorTitle });
          });
        }
      });
  };

  return (
    <Box display="flex" sx={sxFormTab} component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" justifyContent="space-between" flexDirection="column" gap={3}>
        <Typography variant="h6">Create User</Typography>

        <Controller
          name="firstName"
          control={control}
          defaultValue={firstName}
          render={({ field }) => (
            <TextField
              fullWidth
              label="First Name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              required
              {...field}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          defaultValue={lastName}
          render={({ field }) => (
            <TextField
              fullWidth
              label="Last Name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              required
              {...field}
            />
          )}
        />

        <TextField
          fullWidth
          label="Email Address"
          slotProps={{ htmlInput: { ...register('email') } }}
          error={!!errors.email}
          helperText={errors.email?.message}
          required
        />

        <TextField
          fullWidth
          label="User ID"
          slotProps={{ htmlInput: { ...register('userName') } }}
          error={!!errors.userName}
          helperText={errors.userName?.message}
        />

        <TextField
          fullWidth
          label="Auto Assign ID"
          slotProps={{ htmlInput: { ...register('autoAssignId') } }}
          error={!!errors.userName}
          helperText={errors.userName?.message}
        />

        <Controller
          name="locationId"
          control={control}
          render={({ field }) => <AutocompleteLocation value={field.value} onLocationChange={field.onChange} />}
        />

        <Controller
          name="sendPasswordEmail"
          control={control}
          defaultValue={settingsValue.SendPasswordEmailEnable}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={field.onChange} />}
              label="Send this user a welcome email upon creation"
            />
          )}
        />
      </Box>

      <Box sx={{ ...sxPT20 }} display="flex" gap={2}>
        <Button variant="outlined" sx={sxButton} size="large" fullWidth onClick={changeTab}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={sxButton}
          size="large"
          disabled={isLoading}
          fullWidth
        >
          Create User
        </Button>
      </Box>
    </Box>
  );
};

export default Form;
